import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogTrigger, DialogContent } from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
  SheetClose,
} from "@/components/ui/sheet";
import { ArrowLeft, Edit, Calendar, Plus } from "lucide-react";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import { APIClient, HandleError } from "@/api/axiosClient";
import { BrandApi } from "@/api/brand.api";
import { RedemptionMerchantApi } from "@/api/redemptionMerchant.api";
import { PlatformCategoryApi } from "@/api/platformCategory.api";
import { GiftCardProductDetailSkeleton } from "@/components/transactions/GiftCardProductDetailSkeleton";

const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;

const CURRENCY_OPTIONS = [
  {
    iso3CurrencyCode: "USD",
    currencyName: "United States Dollar",
    currencySymbol: "$",
    id: 1,
  },
  { iso3CurrencyCode: "EUR", currencyName: "Euro", currencySymbol: "€", id: 2 },
  {
    iso3CurrencyCode: "GBP",
    currencyName: "British Pound",
    currencySymbol: "£",
    id: 3,
  },
  {
    iso3CurrencyCode: "INR",
    currencyName: "Indian Rupee",
    currencySymbol: "₹",
    id: 4,
  },
  {
    iso3CurrencyCode: "AED",
    currencyName: "United Arab Emirates Dirham",
    currencySymbol: "د.إ",
    id: 5,
  },
  {
    iso3CurrencyCode: "ETB",
    currencyName: "Ethiopian Birr",
    currencySymbol: "Br",
    id: 6,
  },
];

const GiftCardProductView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // Edit sheet state
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // Form state for edit
  const [editForm, setEditForm] = useState({
    brandId: "",
    redemptionMerchantId: "",
    productCode: "",
    productName: "",
    giftCardType: "",
    validityInMonths: "",
    minValue: "",
    maxValue: "",
    denominationIncrement: "",
    loyyalCommissionPercentage: "",
    fulfilmentType: "",
  });
  const [editProductDesc, setEditProductDesc] = useState("");
  const [editHowToUse, setEditHowToUse] = useState("");
  const [editHtmlText, setEditHtmlText] = useState("");
  const [editFiles, setEditFiles] = useState([]);
  const [editImagePreviews, setEditImagePreviews] = useState<string[]>([]);
  const [editSelectedCategory, setEditSelectedCategory] = useState(null);
  const [editSelectedCurrency, setEditSelectedCurrency] = useState(null);

  // Data for dropdowns
  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [merchants, setMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<number[]>([]);

  useEffect(() => {
    async function fetchProduct() {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE}/product/getGiftCardProduct?productId=${id}`,
          { credentials: "include" }
        );
        const data = await res.json();
        setProduct(data || null);
      } catch (err) {
        toast.error("Failed to fetch product details");
      } finally {
        setLoading(false);
      }
    }
    if (id) fetchProduct();
  }, [id]);

  // Fetch brands and merchants when component mounts
  useEffect(() => {
    fetchBrands();
    fetchMerchants();
  }, []);

  // Debug: Log form state changes
  useEffect(() => {
    console.log("Edit form state:", editForm);
    console.log("Selected currency:", editSelectedCurrency);
    console.log("Selected category:", editSelectedCategory);
  }, [editForm, editSelectedCurrency, editSelectedCategory]);

  // Helper functions for edit
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setEditFiles(selectedFiles);
    // Clean up previous previews
    editImagePreviews.forEach((url) => URL.revokeObjectURL(url));
    // Generate previews
    const previews = selectedFiles.map((file) =>
      file instanceof File ? URL.createObjectURL(file) : ""
    );
    setEditImagePreviews(previews);
  };

  const fetchBrands = async () => {
    setBrandsLoading(true);
    try {
      const { data } = await BrandApi.fetchAllBrands();
      setBrands(data || []);
    } catch (err) {
      setBrands([]);
      toast.error("Failed to fetch brands");
    } finally {
      setBrandsLoading(false);
    }
  };

  const fetchMerchants = async () => {
    setMerchantsLoading(true);
    try {
      const { data } =
        await RedemptionMerchantApi.fetchAllRedemptionMerchants();
      setMerchants(data?.redemptionMerchantVOS || []);
    } catch (err) {
      setMerchants([]);
      toast.error("Failed to fetch merchants");
    } finally {
      setMerchantsLoading(false);
    }
  };

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const { data } = await PlatformCategoryApi.fetchCategoryTree();
      setCategories(data?.subCategoryVOs || []);

      // If we have a selected category ID, find the full category object
      if (editSelectedCategory?.id && !editSelectedCategory.categoryName) {
        const foundCategory = findCategoryById(
          data?.subCategoryVOs || [],
          editSelectedCategory.id
        );
        if (foundCategory) {
          setEditSelectedCategory(foundCategory);
        }
      }
    } catch (err) {
      setCategories([]);
      toast.error("Failed to fetch categories");
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Helper function to find category by ID recursively
  const findCategoryById = (categories, targetId) => {
    for (const category of categories) {
      if (category.id === targetId) {
        return category;
      }
      if (category.subCategoryVOs && category.subCategoryVOs.length > 0) {
        const found = findCategoryById(category.subCategoryVOs, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const renderCategories = (categories) => (
    <div className="flex flex-col gap-2">
      {categories.map((cat) => {
        const isExpanded = expandedCategoryIds.includes(cat.id);
        const hasSub = cat.subCategoryVOS && cat.subCategoryVOS.length > 0;
        return (
          <div key={cat.id} className="border rounded p-2">
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => {
                if (hasSub) {
                  setExpandedCategoryIds((ids) =>
                    isExpanded
                      ? ids.filter((id) => id !== cat.id)
                      : [...ids, cat.id]
                  );
                } else {
                  setEditSelectedCategory(cat);
                  setCategoryDialogOpen(false);
                }
              }}
            >
              <span className="flex-1">{cat.categoryName}</span>
              {hasSub && <span>{isExpanded ? "▼" : "▶"}</span>}
              <input
                type="radio"
                name="category"
                checked={editSelectedCategory?.id === cat.id}
                onChange={() => {
                  setEditSelectedCategory(cat);
                  setCategoryDialogOpen(false);
                }}
                onClick={(e) => e.stopPropagation()}
                readOnly
              />
            </div>
            {hasSub && isExpanded && (
              <div className="ml-4 mt-2">
                {renderCategories(cat.subCategoryVOS)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const populateEditForm = () => {
    if (product) {
      console.log("Product data for edit form:", {
        giftCardType: product.giftCardType,
        currencyVO: product.currencyVO,
        brandVO: product.brandVO,
        platformCategoryIds: product.platformCategoryIds,
      });

      setEditForm({
        brandId: product.brandVO?.id?.toString() || "",
        redemptionMerchantId:
          product.redemptionMerchantVO?.id?.toString() || "",
        productCode: product.productCode || "",
        productName: product.productName || "",
        giftCardType: product.giftCardType || "",
        validityInMonths: product.validityInMonths?.toString() || "",
        minValue: product.minValue?.toString() || "",
        maxValue: product.maxValue?.toString() || "",
        denominationIncrement: product.denominationIncrement?.toString() || "",
        loyyalCommissionPercentage:
          product.loyyalCommissionPercentage?.toString() || "",
        fulfilmentType: product.fulfilmentType || "",
      });
      setEditProductDesc(product.productDescription || "");
      setEditHowToUse(product.howToUse || "");
      setEditHtmlText(product.termsAndConditions || "");
      // Set platform category - will be populated with real data when categories are fetched
      const categoryId = product.platformCategoryIds?.[0];
      setEditSelectedCategory(categoryId ? { id: categoryId } : null);
      setEditSelectedCurrency(
        product.currencyVO
          ? CURRENCY_OPTIONS.find((c) => c.id === product.currencyVO.id)
          : null
      );

      console.log("Form values set:", {
        giftCardType: product.giftCardType || "",
        currencyId: product.currencyVO?.id,
        selectedCurrency: product.currencyVO
          ? CURRENCY_OPTIONS.find((c) => c.id === product.currencyVO.id)
          : null,
      });
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const updatedFormData = {
        id: product.id, // Include the product ID for update
        brandVO: { id: editForm?.brandId },
        redemptionMerchantVO: { id: editForm?.redemptionMerchantId },
        productCode: editForm?.productCode,
        productName: editForm?.productName,
        productDescription: editProductDesc,
        giftCardType: editForm?.giftCardType,
        validityInMonths: editForm?.validityInMonths,
        termsAndConditions: editHtmlText,
        howToUse: editHowToUse,
        minValue: editForm?.minValue,
        maxValue: editForm?.maxValue,
        denominationIncrement: editForm?.denominationIncrement,
        productType: "GIFT_CARD",
        currencyVO: editSelectedCurrency
          ? { id: editSelectedCurrency.id }
          : undefined,
        loyyalCommissionPercentage: editForm?.loyyalCommissionPercentage,
        fulfilmentType: editForm?.fulfilmentType,
        platformCategoryIds: editSelectedCategory
          ? [editSelectedCategory.id]
          : [],
      };

      const formData = new FormData();
      if (editFiles?.length > 0) {
        editFiles.forEach((file) => {
          formData.append("giftCardProductLogo", file);
        });
      }
      const blob = new Blob([JSON.stringify(updatedFormData)], {
        type: "application/json",
      });
      formData.append("giftCardProductVO", blob);

      await APIClient("/product/createOrUpdateGiftCardProduct", formData, {
        requestType: "post",
        contentType: "multipart/form-data",
      });

      toast.success("Gift card updated successfully");
      setIsEditSheetOpen(false);

      // Navigate back to gift cards products screen after successful update
      navigate("/rewardpoint/products");
    } catch (error) {
      toast.error(HandleError(error));
    } finally {
      setEditLoading(false);
    }
  };

  const formatRelativeTime = (date) => {
    if (!date) return "";
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return "";
    }
  };

  const getFileUrl = (uri) => {
    if (!uri) return "/placeholder.svg";
    if (uri.startsWith("http")) return uri;
    if (uri.startsWith("/")) {
      return `${API_BASE}${uri}`;
    }
    return `${API_BASE}/file/download?fileUri=${encodeURIComponent(uri)}`;
  };

  if (loading) return <GiftCardProductDetailSkeleton />;
  if (!product) return <div className="p-8">Product not found.</div>;

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <button onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </button>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Product Details</h1>
          <p className="text-gray-500">View and manage product information</p>
        </div>
      </div>

      {/* Product header card */}
      <Card className="border-0 shadow-sm overflow-hidden bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center">
                <img
                  src={getFileUrl(product.productImageUri)}
                  alt={product.productName}
                  className="max-h-16 max-w-16 object-contain"
                  onError={(e) => {
                    e.currentTarget.src = "/placeholder.svg";
                  }}
                />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {product.productName}
                </h2>
                <div className="flex items-center gap-3 mt-1 text-gray-500">
                  <span className="text-sm">Code: {product.productCode}</span>
                  {product.createdDate && (
                    <span className="text-sm flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Created around{" "}
                      {formatRelativeTime(product.createdDate)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-base font-medium flex items-center gap-2"
              onClick={async () => {
                populateEditForm();
                // Fetch categories first and set the real category name before opening sheet
                await fetchCategories();
                setIsEditSheetOpen(true);
              }}
            >
              <Edit className="h-4 w-4 mr-1" /> Edit Product
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for different sections */}
      <Tabs defaultValue="details" className="w-full">
        <TabsList className="bg-white border-b border-gray-200 w-full justify-start rounded-none h-12 px-6">
          <TabsTrigger
            value="details"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Gift Card Details
          </TabsTrigger>
        </TabsList>

        {/* Gift Card Details Tab */}
        <TabsContent value="details" className="space-y-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* General Information */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Product Name
                    </p>
                    <p className="text-gray-800">{product.productName}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Product Code
                    </p>
                    <p className="text-gray-800">{product.productCode}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Brand</p>
                    <p className="text-gray-800">
                      {product.brandVO?.brandName || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Merchant
                    </p>
                    <p className="text-gray-800">
                      {product.redemptionMerchantVO?.merchantName || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Product Description
                    </p>
                    <p className="text-gray-800">
                      {product.productDescription || "-"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Gift Card Details */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Gift Card Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Gift Card Type
                    </p>
                    <p className="text-gray-800">
                      {product.giftCardType || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Validity (Months)
                    </p>
                    <p className="text-gray-800">
                      {product.validityInMonths || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Fulfilment Type
                    </p>
                    <p className="text-gray-800">
                      {product.fulfilmentType || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Platform Categories
                    </p>
                    <p className="text-gray-800">
                      {product.platformCategoryIds?.join(", ") || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      How To Use
                    </p>
                    <div className="text-gray-800 prose prose-sm max-w-none">
                      {product.howToUse ? (
                        <div
                          dangerouslySetInnerHTML={{ __html: product.howToUse }}
                        />
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing & Terms */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Pricing & Terms
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Minimum Value
                    </p>
                    <p className="text-gray-800">{product.minValue || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Maximum Value
                    </p>
                    <p className="text-gray-800">{product.maxValue || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Denomination Increment
                    </p>
                    <p className="text-gray-800">
                      {product.denominationIncrement || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Currency
                    </p>
                    <p className="text-gray-800">
                      {product.currencyVO?.currencyName || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Loyyal Commission (%)
                    </p>
                    <p className="text-gray-800">
                      {product.loyyalCommissionPercentage || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Terms & Conditions
                    </p>
                    <div className="text-gray-800 prose prose-sm max-w-none">
                      {product.termsAndConditions ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: product.termsAndConditions,
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Product Sheet */}
      <Sheet open={isEditSheetOpen} onOpenChange={setIsEditSheetOpen}>
        <SheetContent className="sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit Gift Card Product</SheetTitle>
            <SheetDescription>
              Make changes to your gift card product here. Click save when
              you're done.
            </SheetDescription>
          </SheetHeader>

          <div className="py-6">
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid grid-cols-3 mb-6">
                <TabsTrigger value="general">General Information</TabsTrigger>
                <TabsTrigger value="gift-card">Gift Card Details</TabsTrigger>
                <TabsTrigger value="pricing">Pricing & Terms</TabsTrigger>
              </TabsList>

              <form onSubmit={handleEditSubmit}>
                {/* General Information Tab */}
                <TabsContent value="general" className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Logo
                    </label>
                    <div className="w-28 h-28 rounded-lg bg-gray-100 flex items-center justify-center mb-2 relative">
                      <input
                        type="file"
                        multiple
                        onChange={handleEditFileChange}
                        className="hidden"
                        id="edit-logo-upload"
                      />
                      <label htmlFor="edit-logo-upload">
                        <Button variant="ghost" size="icon" asChild>
                          <span>
                            <Plus className="h-8 w-8 text-gray-400" />
                          </span>
                        </Button>
                      </label>
                      {editImagePreviews.length > 0 && (
                        <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-1">
                          {editImagePreviews.map((src, idx) => (
                            <img
                              key={idx}
                              src={src}
                              alt={`Preview ${idx + 1}`}
                              className="w-24 h-24 object-contain rounded"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Brand*
                    </label>
                    <Select
                      name="brandId"
                      value={editForm.brandId}
                      onValueChange={(v) =>
                        setEditForm((prev) => ({ ...prev, brandId: v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Brand" />
                      </SelectTrigger>
                      <SelectContent>
                        {brandsLoading ? (
                          <div className="px-3 py-2 text-gray-500">
                            Loading...
                          </div>
                        ) : brands.length === 0 ? (
                          <div className="px-3 py-2 text-gray-500">
                            No brands ({brands.length})
                          </div>
                        ) : (
                          brands.map((b) => (
                            <SelectItem key={b.id} value={b.id + ""}>
                              {b.brandName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Merchant*
                    </label>
                    <Select
                      name="redemptionMerchantId"
                      value={editForm.redemptionMerchantId}
                      onValueChange={(v) =>
                        setEditForm((prev) => ({
                          ...prev,
                          redemptionMerchantId: v,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Merchant" />
                      </SelectTrigger>
                      <SelectContent>
                        {merchantsLoading ? (
                          <div className="px-3 py-2 text-gray-500">
                            Loading...
                          </div>
                        ) : merchants.length === 0 ? (
                          <div className="px-3 py-2 text-gray-500">
                            No merchants
                          </div>
                        ) : (
                          merchants.map((m) => (
                            <SelectItem key={m.id} value={m.id + ""}>
                              {m.merchantName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Name*
                    </label>
                    <Input
                      name="productName"
                      placeholder="Enter product name"
                      value={editForm.productName}
                      onChange={handleEditInputChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Code*
                    </label>
                    <Input
                      name="productCode"
                      placeholder="Enter product code.."
                      value={editForm.productCode}
                      onChange={handleEditInputChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Description
                    </label>
                    <Textarea
                      placeholder="Enter product description"
                      rows={4}
                      value={editProductDesc}
                      onChange={(e) => setEditProductDesc(e.target.value)}
                    />
                  </div>
                </TabsContent>

                {/* Gift Card Details Tab */}
                <TabsContent value="gift-card" className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Validity* <span className="text-xs">(Months)</span>
                    </label>
                    <Input
                      name="validityInMonths"
                      placeholder="Validity in months"
                      value={editForm.validityInMonths}
                      onChange={handleEditInputChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type*
                    </label>
                    <Select
                      name="giftCardType"
                      value={editForm.giftCardType?.toLowerCase() || ""}
                      onValueChange={(v) =>
                        setEditForm((prev) => ({ ...prev, giftCardType: v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="online">Online</SelectItem>
                        <SelectItem value="instore">In-store</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gift Code Fulfillment*
                    </label>
                    <Select
                      name="fulfilmentType"
                      value={editForm.fulfilmentType}
                      onValueChange={(v) =>
                        setEditForm((prev) => ({ ...prev, fulfilmentType: v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Fulfillment Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MERCHANT_API">
                          MERCHANT_API
                        </SelectItem>
                        <SelectItem value="MERCHANT_EMAIL">
                          MERCHANT_EMAIL
                        </SelectItem>
                        <SelectItem value="LOYYAL_GENERATED">
                          LOYYAL_GENERATED
                        </SelectItem>
                        <SelectItem value="MERCHANT_UPLOADED">
                          MERCHANT_UPLOADED
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Platform Category
                    </label>
                    <Dialog
                      open={categoryDialogOpen}
                      onOpenChange={setCategoryDialogOpen}
                    >
                      <DialogTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full text-left"
                          onClick={fetchCategories}
                        >
                          {editSelectedCategory?.categoryName || "Select"}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md w-full p-6">
                        <div className="rounded-xl border p-6 bg-white">
                          {categoriesLoading ? (
                            <div className="text-center text-gray-500 py-8">
                              Loading...
                            </div>
                          ) : (
                            renderCategories(categories)
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      How To Use
                    </label>
                    <Textarea
                      placeholder="Enter how to use"
                      rows={6}
                      value={editHowToUse}
                      onChange={(e) => setEditHowToUse(e.target.value)}
                    />
                  </div>
                </TabsContent>

                {/* Pricing & Terms Tab */}
                <TabsContent value="pricing" className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Denomination
                    </label>
                    <div className="flex gap-2">
                      <Input
                        name="minValue"
                        placeholder="Min value"
                        className="flex-1"
                        value={editForm.minValue}
                        onChange={handleEditInputChange}
                      />
                      <Input
                        name="maxValue"
                        placeholder="Max value"
                        className="flex-1"
                        value={editForm.maxValue}
                        onChange={handleEditInputChange}
                      />
                      <Input
                        name="denominationIncrement"
                        placeholder="Increment"
                        className="flex-1"
                        value={editForm.denominationIncrement}
                        onChange={handleEditInputChange}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency*
                    </label>
                    <Select
                      value={editSelectedCurrency?.id?.toString() || ""}
                      onValueChange={(v) =>
                        setEditSelectedCurrency(
                          CURRENCY_OPTIONS.find((c) => c.id === Number(v))
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id.toString()}>
                            {opt.currencyName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Loyyal Commission* (%)
                    </label>
                    <Input
                      name="loyyalCommissionPercentage"
                      placeholder="Commission"
                      value={editForm.loyyalCommissionPercentage}
                      onChange={handleEditInputChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Terms & Conditions
                    </label>
                    <Textarea
                      placeholder="Enter terms & conditions"
                      rows={4}
                      value={editHtmlText}
                      onChange={(e) => setEditHtmlText(e.target.value)}
                    />
                  </div>
                </TabsContent>

                <SheetFooter className="mt-6">
                  <SheetClose asChild>
                    <Button type="button" variant="outline">
                      Cancel
                    </Button>
                  </SheetClose>
                  <Button type="submit" disabled={editLoading}>
                    {editLoading ? "Updating..." : "Update Product"}
                  </Button>
                </SheetFooter>
              </form>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default GiftCardProductView;
